import React, { useState, useRef, useEffect } from 'react';
import { get } from 'lodash';
import { PropTypes } from 'prop-types';

import Analytics from 'analytics-opencare';
import { Form } from 'opencare-crown';
import scrollToRef from 'utils/scrollToRef';

import { StyledFormItem } from 'common/styles/oc-styled-components';

import { useSelector } from 'react-redux';

import { STEP_KEY, generateStepAnalytics } from '../../../../utils';
import { StepWrapper } from '../../../../styled-components';
import {
  Title,
  Subtitle,
  OptionBlock,
  AdditionalBlock,
} from '../../../../../common/styled-components';
import SingleSelect from '../../../../../common/components/SingleSelect/SingleSelect';
import { useFunnelStorage } from '../../../../storage/Storage';
import { SUBSTEPS } from '../../constants';
import InsuranceProviderPickerUS from '../../../../../common/components/InsurancePickerUS/InsuranceProviderPickerUS';
import { SelectSkeleton } from '../../../../../common/components/InsurancePickerUS/styled-components';

import { selectDataInsuranceList } from '../../../../../../connect/selectors';

const analytics = new Analytics();

const AmericaInsuranceProvider = ({ position, setSubStep }) => {
  const storage = useFunnelStorage();
  const [insuranceForm] = Form.useForm();
  const refToOtherDropdown = useRef();

  const savedInsuranceProgress = storage.get(STEP_KEY.INSURANCE);
  const savedLocation = storage.get(STEP_KEY.LOCATION);
  const region = get(
    savedLocation,
    'address.administrative_area_level_1_short',
  );

  const insuranceList = useSelector(selectDataInsuranceList);

  const [topProviders, setTopProviders] = useState(null);

  useEffect(() => {
    if (!insuranceList) return;

    setTopProviders(
      insuranceList
        .filter(provider => provider.isPopular)
        .sort((a, b) => a.name.localeCompare(b.name)),
    );
  }, [insuranceList]);
   
  const [provider, setProvider] = useState(
    get(savedInsuranceProgress, 'providerId')
      ? {
        id: savedInsuranceProgress.providerId,
        name: savedInsuranceProgress.provider,
      }
      : null,
  );
  const [showDropdown, setShowDropdown] = useState(
    get(savedInsuranceProgress, 'hasInsurance') === 'Yes'
      ? get(savedInsuranceProgress, 'topProvider') === false
      : false,
  );

  useEffect(() => {
    if (showDropdown) {
      scrollToRef(refToOtherDropdown);
    }
  }, [showDropdown]);

  const updateInsuranceProvider = selectedProvider => {
    setProvider(selectedProvider);

    if (selectedProvider) {
      analytics.trackClick({
        pageType: analytics.pageTypes.FUNNEL,
        pageName: 'funnel_insurance-provider',
        name: 'wizard',
        section: 'insurance-provider',
        ...generateStepAnalytics(position, storage, {
          response: selectedProvider.name,
        }),
      });

      storage.save(STEP_KEY.INSURANCE, {
        ...savedInsuranceProgress,
        hasInsurance: 'Yes',
        provider: selectedProvider.name,
        providerId: selectedProvider.id,
        plan: null,
        planId: null,
        topProvider:
          topProviders.find(p => p.id === selectedProvider.id) !== undefined,
      });
      setSubStep(SUBSTEPS.PLANS);
    }
  };

  return (
    <div data-testid="united-states">
      <Title id="title">Who is your dental insurance provider?</Title>
      <Subtitle>
        If you're not sure - don't worry - you can change your answer later.
      </Subtitle>
      <StepWrapper>
        {!topProviders 
          && (
            <StyledFormItem margin={0}>
              <div data-testid="ProviderSkeleton">
                <SelectSkeleton active margin="0 10px 0 0" paragraph={false} />
              </div>
            </StyledFormItem>
          )
        }
        {topProviders 
          && (
            <Form form={insuranceForm} onFinish={updateInsuranceProvider}>
              <OptionBlock row={4}>
                {topProviders.map(insurance => (
                  <SingleSelect
                    key={insurance.id}
                    id={insurance.id}
                    title={insurance.name}
                    describedBy="title"
                    onChange={() => updateInsuranceProvider(insurance)}
                    minHeightDesktop="84px"
                    selected={
                      get(savedInsuranceProgress, 'provider')
                      === insurance.name && !showDropdown
                    }
                  />
                ))}

                <SingleSelect
                  key="other-provider"
                  id="other-provider"
                  title="Other provider..."
                  describedBy="title"
                  minHeightDesktop="84px"
                  onChange={() => setShowDropdown(!showDropdown)}
                  selected={
                    showDropdown
                    || get(savedInsuranceProgress, 'topProvider') === false
                  }
                />
              </OptionBlock>

              {showDropdown 
                && (
                  <AdditionalBlock
                    data-testid="other-dropdown"
                    ref={refToOtherDropdown}
                    tabIndex="-1"
                  >
                    <InsuranceProviderPickerUS
                      form={insuranceForm}
                      region={region}
                      updateInsuranceProvider={updateInsuranceProvider}
                      defaultProvider={provider}
                    />
                  </AdditionalBlock>
                )
              }
            </Form>
          )
        }
      </StepWrapper>
    </div>
  );
};

AmericaInsuranceProvider.defaultProps = {
  position: '',
};

AmericaInsuranceProvider.propTypes = {
  position: PropTypes.string,
  setSubStep: PropTypes.func.isRequired,
};

export default AmericaInsuranceProvider;
